/**
 * Regeneration types and constants for cover/book regeneration feature.
 * Used by client (preview UI), server (API endpoints), and graph (state routing).
 */
import { z } from 'zod';
import { SHOT_TYPES } from './storyOutline.js';

/**
 * Canonical regeneration scope used across API, storage backup, and graph state.
 * - 'cover': Regenerate only the cover image + PDFs (reuses existing pages)
 * - 'book': Regenerate all pages, images, cover, and PDFs from the approved plan
 * - 'page': Regenerate a single targeted page image + PDFs (reuses other pages)
 */
export const REGENERATION_SCOPES = ['cover', 'book', 'page'] as const;
export type RegenerationScope = (typeof REGENERATION_SCOPES)[number];

export const regenerationScopeSchema = z.enum(REGENERATION_SCOPES);

/**
 * Regeneration limits.
 * Initial generation counts as 1, so:
 * - Cover: 3 total = 1 initial + 2 regenerations
 * - Book: 2 total = 1 initial + 1 regeneration
 * - Page: 6 total = 1 initial + 5 regenerations (per-page counter)
 */
export const COVER_REGEN_LIMIT = 3;
export const BOOK_REGEN_LIMIT = 2;
export const PAGE_REGEN_LIMIT = 6;

/**
 * Admin-only per-page overrides — shared verbatim between the wire request
 * ({@link regeneratePageRequestSchema}) and the durable in-flight record
 * ({@link regenInFlightSchema}), so the two can never drift on what an
 * override is allowed to contain. `.strict()` is used so unknown override
 * keys are rejected at validation time.
 */
export const pageOverridesSchema = z
  .object({
    imagePrompt: z.string().optional(),
    text: z.string().optional(),
    shotType: z.enum(SHOT_TYPES).optional(),
    setting: z.string().optional(),
  })
  .strict()
  .partial();
export type PageOverrides = z.infer<typeof pageOverridesSchema>;

/** Maximum length of the customer's free-form regeneration instructions. */
export const REGEN_INSTRUCTIONS_MAX_LENGTH = 1000;

/**
 * Optional free-form steering text, shared by all three regeneration request
 * schemas (and the admin page route, which parses the page schema).
 */
export const regenerationInstructionsSchema = z
  .string()
  .max(REGEN_INSTRUCTIONS_MAX_LENGTH)
  .optional();

/**
 * Request body for POST /api/orders/:id/regenerate-page (and the admin equivalent).
 * - `pageIndex`: zero-based index of the page to regenerate.
 * - `additionalPrompt`: optional free-form text appended to the page imagePrompt
 *   to steer the regeneration (e.g. "make the pie whole, no slice in his hand").
 * - `pageOverrides`: admin-only — direct edits to the page JSON before re-running.
 *   The user-facing route strips/rejects this field; the admin route honors it.
 * - `forceFail`: MOCK_MODE-only — when true, the mock runner deliberately fails
 *   the page regeneration so E2E tests can drive the failure-restore code path
 *   (issue #500). The server gates this strictly: outside MOCK_MODE the field
 *   is ignored.
 */
export const regeneratePageRequestSchema = z.object({
  pageIndex: z.number().int().nonnegative(),
  additionalPrompt: regenerationInstructionsSchema,
  pageOverrides: pageOverridesSchema.optional(),
  forceFail: z.boolean().optional(),
});

export type RegeneratePageRequest = z.infer<typeof regeneratePageRequestSchema>;

/**
 * Request body for POST /api/orders/:id/regenerate-cover.
 *
 * - `additionalPrompt`: optional customer instructions (<= 1000 chars).
 * - `forceFail`: MOCK_MODE-only — the server ignores it strictly outside
 *   MOCK_MODE. Used by E2E tests (issue #500) to drive the failure-restore
 *   banner without touching real generation models.
 */
export const regenerateCoverRequestSchema = z.object({
  additionalPrompt: regenerationInstructionsSchema,
  forceFail: z.boolean().optional(),
});

export type RegenerateCoverRequest = z.infer<
  typeof regenerateCoverRequestSchema
>;

/**
 * Request body for POST /api/orders/:id/regenerate-book.
 *
 * - `additionalPrompt`: optional customer instructions (<= 1000 chars).
 * - `forceFail`: MOCK_MODE-only — the server ignores it strictly outside
 *   MOCK_MODE. Used by E2E tests (issue #500) to drive the failure-restore
 *   banner without touching real generation models.
 */
export const regenerateBookRequestSchema = z.object({
  additionalPrompt: regenerationInstructionsSchema,
  forceFail: z.boolean().optional(),
});

export type RegenerateBookRequest = z.infer<typeof regenerateBookRequestSchema>;

/**
 * Error codes returned by regeneration endpoints.
 *
 * `NOT_PUBLISHED` and `ALREADY_REGENERATING` are increment-two additions
 * (Task 25) — the two customer-facing reasons
 * `book/revisions.ts`'s `RegenerationClaimError` can carry (its own third
 * reason, `LIMIT_REACHED`, already had a code). `CHECKPOINT_UNAVAILABLE` is
 * kept for the historical MSW fixtures in `@woolaby/test-utils` — this
 * codebase's `book/` regeneration has no LangGraph checkpoint concept, so
 * no current route ever emits it.
 */
export const REGENERATION_ERROR_CODES = [
  'ALREADY_PRINTING',
  'LIMIT_REACHED',
  'CHECKPOINT_UNAVAILABLE',
  'NOT_PUBLISHED',
  'ALREADY_REGENERATING',
] as const;

export type RegenerationErrorCode = (typeof REGENERATION_ERROR_CODES)[number];

/**
 * Response from POST /api/orders/:id/regenerate-cover
 * Response from POST /api/orders/:id/regenerate-book
 * Response from POST /api/orders/:id/regenerate-page
 *
 * `runId` is an increment-two addition (Task 25) — the operation's current
 * `orders.workflow_run_id`, the same column the sibling `/start-generation`
 * / `/try-again` / `/start-over` routes key their own `{ runId }` response
 * off. A regeneration operation is NOT itself a `bookWorkflow` run (see
 * `book/revisions.ts`'s `claimRegenerationOperation` docblock), so this id
 * is not observable through `/api/mastra/*` — it exists so every mutating
 * `book/routes.ts` endpoint returns the same wire shape. Additive: existing
 * callers reading only `status` are unaffected.
 */
export const regenerateResponseSchema = z.object({
  status: z.literal('started'),
  runId: z.string(),
});

export type RegenerateResponse = z.infer<typeof regenerateResponseSchema>;

/**
 * Error response from regeneration endpoints.
 */
export const regenerateErrorResponseSchema = z.object({
  error: z.enum(REGENERATION_ERROR_CODES),
  message: z.string(),
});

export type RegenerateErrorResponse = z.infer<
  typeof regenerateErrorResponseSchema
>;

/**
 * Failure metadata persisted on the order row when a regeneration run fails
 * but the previously-completed book has been restored from backup. Surfaces
 * the dismissible banner on `/book/preview/<id>` (issue #500).
 *
 * `failedAt` is an ISO-8601 timestamp.
 */
export const lastRegenFailureSchema = z.object({
  scope: regenerationScopeSchema,
  failureReason: z.string(),
  failureContext: z.string(),
  failureMessage: z.string().nullable(),
  failedAt: z.string(),
});

export type LastRegenFailure = z.infer<typeof lastRegenFailureSchema>;

/**
 * Durable regeneration OPERATION context — persisted on
 * `orders.regen_in_flight` atomically with the claim that mints the
 * candidate `book_revisions` row, and cleared on completion (publish or
 * failure-restore). It records everything a failure restore needs (the
 * refund flag, the prior published revision, font and judgments) so an
 * operation interrupted by a crash can be ROLLED BACK by the server's
 * recovery sweep without guessing, and the scope/feedback the original claim
 * captured. The shape is a clean replacement, not a port of the LangGraph-era
 * record: there is no LangGraph checkpoint, no
 * thread id and no `backupOrderArtifacts` file-naming convention in the
 * Mastra-native `book/` workflow this record now serves. See
 * `apps/server/src/book/regeneration.ts`.
 *
 * `pageIndex` is 0-based, matching {@link regeneratePageRequestSchema}
 * directly — there is no file-naming convention here that would need a
 * 1-based conversion.
 *
 * `feedback` and `pageOverrides` are the original request arguments,
 * preserved VERBATIM and read back by every draw the operation makes — never
 * re-derived, never re-prompted.
 */
export const regenInFlightSchema = z.object({
  /** Identifies this operation across a crash and its recovery rollback — never regenerated. */
  operationId: z.string().uuid(),
  scope: regenerationScopeSchema,
  /** Whether this operation consumes the customer's own quota (`true`) or is an admin redraw that does not (`false`) — also the refund-eligibility flag on failure. */
  decrementCount: z.boolean(),
  /** The published revision this operation was claimed against — the immutable base a candidate is built from. */
  baseRevisionId: z.string().uuid(),
  /** The draft `book_revisions` row this operation is producing. */
  candidateRevisionId: z.string().uuid(),
  /** 'page' scope only — the page targeted for a fresh draw. */
  pageId: z.string().min(1).optional(),
  /** 'page' scope only — the 0-based index the original request named, kept for display/audit alongside `pageId`. */
  pageIndex: z.number().int().nonnegative().optional(),
  /** Verbatim customer/admin steering text for this redraw, or `null` when none was given. */
  feedback: z.string().nullable(),
  /** Admin-only per-page overrides, applied to the candidate's own copy of the frozen script/prose — never the published base. */
  pageOverrides: pageOverridesSchema.nullable().optional(),
  /** "Prior publication" — the revision a failed operation restores visibility to. Always equal to `baseRevisionId`; kept as its own field for clarity at the call sites that read "what do we roll back to". */
  priorPublishedRevisionId: z.string().uuid(),
  /** "Prior font" — the locked page font size a page/cover-scoped operation must preserve untouched. */
  priorFontSize: z.number().int().positive(),
  /** "Prior judgments" — `orders.cover_band_text_shipped_at` as it stood before this operation, so a failure restore never has to guess. */
  priorCoverBandTextShippedAt: z.string().nullable(),
  /**
   * The continuity self-heal BUDGET epoch this operation's
   * continuity sweep is scoped to, resolved ONCE at claim time
   * (`book/revisions.ts`'s `claimRegenerationOperation`) from
   * `orders.continuity_budget_epoch`: bumped for a genuinely fresh attempt,
   * retained verbatim for a retry of one that already failed. Read back
   * verbatim by `book/regeneration.ts`'s three execute* functions — never
   * re-derived, so a same-scope retry never buys a second fresh budget for
   * the same underlying attempt.
   */
  continuityBudgetEpoch: z.number().int().nonnegative(),
  startedAt: z.string(),
  /**
   * MOCK_MODE-only — when true, the operation deliberately fails so E2E can
   * drive the failure-restore path. Stripped strictly outside MOCK_MODE at
   * the claim site, mirroring the historical trigger's own gate.
   */
  forceFail: z.boolean().optional(),
});

export type RegenInFlight = z.infer<typeof regenInFlightSchema>;
